import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Events } from '@ngrx/signals/events'
import { of } from 'rxjs'
import { concatMap, filter, map, pluck, switchMap } from 'rxjs/operators'

import { _Record } from '@app/core/record'
import { conversationEvents } from '@app/modules/messaging/components/store/conversation.events'
import { sendingEvents } from '@app/modules/messaging/components/store/sending.events'
import { NotificationsStore } from '@app/overlays/notification/notifications.store'
import * as fromInteractionLists from '@interaction-lists/ngrx'

import * as fromFeature from '.'
import * as fromNotifications from '../notifications'
import * as fromProfile from '../profile'

@Injectable()
export class InteractionEffects {
  private readonly actions$ = inject(Actions)
  private readonly events = inject(Events)
  private readonly notificationsStore = inject(NotificationsStore)

  readonly notification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNotifications.Actions.received),
      map(note => ({ ...note, group: this.notificationsStore.toGroup(note.notification) })),
      switchMap(({ group, notification }) =>
        group === 'messages'
          ? of(fromFeature.Actions.addSingle({ group: 'commonThreads', id: notification.source_profile_id }))
          : of(fromFeature.Actions.addSingle({ group, id: notification.source_profile_id })),
      ),
    ),
  )

  readonly messageSent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.messageSent),
      map(({ profileId }) => fromFeature.Actions.addSingle({ id: profileId, group: 'commonThreads' })),
    ),
  )

  readonly onConversationLoaded$ = createEffect(() =>
    this.events.on(conversationEvents.loadSuccess).pipe(
      map(({ payload }) => payload.response),
      filter(response => response.conversation.locked && response.messages.length > 0),
      map(response =>
        fromFeature.Actions.removeSingle({ group: 'lockedThreads', id: response.conversation.profile.id }),
      ),
    ),
  )

  readonly onConversationRemoved$ = createEffect(() =>
    this.events.on(conversationEvents.removeSuccess).pipe(
      map(({ payload }) => payload.conversation.profile.id),
      switchMap(id => [
        fromFeature.Actions.removeSingle({ group: 'lockedThreads', id }),
        fromFeature.Actions.removeSingle({ group: 'commonThreads', id }),
      ]),
    ),
  )

  readonly onConversationUnlocked$ = createEffect(() =>
    this.events.on(conversationEvents.unlockSuccess).pipe(
      map(({ payload }) => payload.response.conversation.profile.id),
      map(id => fromFeature.Actions.removeSingle({ group: 'lockedThreads', id })),
    ),
  )

  readonly onMessageSent$ = createEffect(() =>
    this.events.on(sendingEvents.sendMessageSuccess).pipe(
      map(({ payload }) => payload.conversation.profile.id),
      map(id => fromFeature.Actions.addSingle({ group: 'commonThreads', id })),
    ),
  )

  readonly view$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.view),
      map(({ id }) => fromFeature.Actions.addSingle({ id, group: 'visited' })),
    ),
  )

  readonly hide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.hide),
      map(({ id }) =>
        fromFeature.Actions.removeBatch({
          partial: _Record.fromKeys(Object.keys(fromFeature.initialState) as (keyof fromFeature.State)[], [id]),
        }),
      ),
    ),
  )

  readonly fromInteractionList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInteractionLists.Actions.loadSuccess),
      switchMap(({ data }) =>
        of({
          commonThreads: data.profiles_with_common_threads,
          lockedThreads: data.profiles_with_locked_threads,
        }),
      ),
      map(partial => fromFeature.Actions.addBatch({ partial })),
    ),
  )

  readonly onGranted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.accessGranted),
      pluck('id'),
      concatMap(id => [
        fromFeature.Actions.removeSingle({ id, group: 'pending_requesters' }),
        fromFeature.Actions.addSingle({ id, group: 'approved_requesters' }),
        fromFeature.Actions.removeSingle({ id, group: 'declined_requesters' }),
      ]),
    ),
  )

  readonly onUnlocked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.albumAccessUnlocked),
      pluck('id'),
      concatMap(id => [
        fromFeature.Actions.removeSingle({ id, group: 'locked_granters' }),
        fromFeature.Actions.addSingle({ id, group: 'unlocked_granters' }),
      ]),
    ),
  )

  readonly onDeclined$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.accessDeclined),
      pluck('id'),
      concatMap(id => [
        fromFeature.Actions.removeSingle({ id, group: 'pending_requesters' }),
        fromFeature.Actions.removeSingle({ id, group: 'approved_requesters' }),
        fromFeature.Actions.addSingle({ id, group: 'declined_requesters' }),
      ]),
    ),
  )

  readonly favorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.favorited),
      map(({ id }) => fromFeature.Actions.addSingle({ id, group: 'favorites' })),
    ),
  )

  readonly unfavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.unfavorited),
      map(({ id }) => fromFeature.Actions.removeSingle({ id, group: 'favorites' })),
    ),
  )
}
